// The chat text surface: Markdown rendering, RTL detection, copy helpers and
// the fenced-code / assistant-text renderers.
//
// Extracted from src/app.jsx (which stays the composition root and imports
// these back). Behaviour and markup are unchanged.

import { stripFollowupForDisplay } from "../../cloud/followup.js";

const {useState, useMemo} = React;

// ── Markdown rendering for Claude's chat replies ──
function escapeHtml(s){return String(s).replace(/[&<>"']/g,c=>({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));}
// Parsing + sanitising is the single most expensive thing the chat does, and it
// ran on EVERY render of EVERY mounted message (a keystroke in the composer, a
// stream event, the 5s stuck-check). Chat text is immutable once streamed, so the
// exact input string is a sound cache key. Bounded so a long session can't leak:
// Map preserves insertion order, so the oldest entry is the first key.
const MD_CACHE=new Map();
const MD_CACHE_MAX=500;
// Huge blobs are rare, cost the most memory and are least likely to repeat.
const MD_CACHE_MAX_LEN=200000;
function mdToHtml(src){
  const text=String(src==null?"":src);
  const cacheable=text.length<=MD_CACHE_MAX_LEN;
  if(cacheable&&MD_CACHE.has(text))return MD_CACHE.get(text);
  try{
    if(window.marked&&window.DOMPurify){
      const raw=window.marked.parse(text,{breaks:true,gfm:true});
      // Inline CSS is never valid chat content. Keep it out even though the
      // first-party React UI still needs style-src-attr for dynamic layout.
      const clean=window.DOMPurify.sanitize(raw,{
        ADD_ATTR:["target","rel"],
        FORBID_ATTR:["style"],
        FORBID_TAGS:["style"],
      });
      // open links in a new tab
      const html=clean.replace(/<a /g,'<a target="_blank" rel="noopener noreferrer" ');
      if(cacheable){
        MD_CACHE.set(text,html);
        // evict oldest
        if(MD_CACHE.size>MD_CACHE_MAX)MD_CACHE.delete(MD_CACHE.keys().next().value);
      }
      return html;
    }
  }catch(e){}
  // fallback if the markdown libs failed to load: plain text with newlines preserved.
  // Deliberately NOT cached — the libs load async, and a cached fallback would
  // pin a message to plain text for the rest of the session.
  return escapeHtml(text).replace(/\n/g,"<br>");
}
// `dir="auto"` picks a direction from the FIRST strong character, so a Hebrew
// reply that opens with a heading marker, an English word, or a file path renders
// left-to-right and reads broken. Any Hebrew in the text means the message is a
// Hebrew message: force RTL and let the odd Latin word bidi-embed inside it.
const HEB_RE=/[֐-׿יִ-ﭏ]/;
function dirOf(text){ return HEB_RE.test(String(text==null?"":text))?"rtl":"ltr"; }
const Md=React.memo(function Md({text,className,dir}){
  return <div className={(className?className+" ":"")+"md"} dir={dir} dangerouslySetInnerHTML={{__html:mdToHtml(text)}}/>;
});

// Copy `text` to the clipboard. Uses the async Clipboard API where available and
// falls back to a hidden textarea + execCommand for older/insecure contexts.
function copyText(text,html){
  const t=String(text==null?"":text);
  // When an HTML rendering is supplied, write both flavours so pasting into a
  // rich target (docs, email, Slack) keeps the design — headings, bold, lists —
  // instead of raw markdown like "## Title". Plain-text targets still get `t`.
  if(html!=null){
    try{
      if(navigator.clipboard&&window.ClipboardItem&&navigator.clipboard.write){
        const item=new ClipboardItem({
          "text/html":new Blob([String(html)],{type:"text/html"}),
          "text/plain":new Blob([t],{type:"text/plain"}),
        });
        return navigator.clipboard.write([item]);
      }
    }catch(e){}
  }
  try{ if(navigator.clipboard&&navigator.clipboard.writeText)return navigator.clipboard.writeText(t); }catch(e){}
  return new Promise((res,rej)=>{try{
    const ta=document.createElement("textarea");ta.value=t;ta.style.position="fixed";ta.style.top="-1000px";ta.style.opacity="0";
    document.body.appendChild(ta);ta.focus();ta.select();
    const ok=document.execCommand("copy");document.body.removeChild(ta);
    ok?res():rej(new Error("copy failed"));
  }catch(e){rej(e);}});
}
// Flatten a chat's messages into a plain-text transcript for "Copy chat".
// Each turn is labelled with its speaker; attachments are listed by name so the
// pasted copy still records that files were shared even though their bytes aren't.
function chatToText(msgs,agentName,title){
  const who={user:"You"};
  const speaker=(role)=>who[role]||agentName||"Assistant";
  const lines=(msgs||[]).map(m=>{
    const parts=[speaker(m.role)+":"];
    if(m.files&&m.files.length)parts.push(m.files.map(f=>"[attached: "+(f.name||"file")+"]").join("\n"));
    // User turns carry their body in m.text; assistant turns keep theirs in
    // m.parts (text + tool parts interleaved). Only the text parts belong in a
    // transcript — same join the per-message copy button uses.
    const body=m.text&&String(m.text).trim()
      ?String(m.text).trim()
      :stripFollowupForDisplay((m.parts||[]).filter(pt=>pt.type==="text").map(pt=>pt.text||"").join("\n\n")).trim();
    if(body)parts.push(body);
    return parts.join("\n");
  });
  const head=title&&title.trim()?title.trim()+"\n\n":"";
  return head+lines.join("\n\n———\n\n");
}
// A small copy button that flips to "✓ Copied" for ~1.2s after a click.
function CopyBtn({text,html,label,title,cls,doneLabel}){
  const[done,setDone]=useState(false);
  const on=(e)=>{e.stopPropagation();copyText(text,html).then(()=>{setDone(true);setTimeout(()=>setDone(false),1200);}).catch(()=>{});};
  return <button type="button" className={cls||"copybtn"} title={title||"Copy to clipboard"} aria-label={done?"Copied":(title||"Copy to clipboard")} onClick={on}>
    <span aria-hidden="true">{done?(doneLabel||"✓ Copied"):(label||"📋 Copy")}</span></button>;
}
// Languages whose fenced code blocks are safe to type straight into a project's
// bash terminal (so they get a "Run in terminal" button, not just Copy).
const SHELL_LANGS=new Set(["","bash","sh","shell","zsh","console","terminal","cmd","shell-session","shellscript"]);
const isShellLang=(l)=>SHELL_LANGS.has(String(l||"").toLowerCase().trim());
// Split assistant text into prose and fenced ``` code blocks, so each code block
// renders with its own Copy (and, for shell commands, Run-in-terminal) button
// instead of being swallowed into the markdown HTML.
function splitCodeBlocks(text){
  const src=String(text==null?"":text);
  const re=/```([^\n`]*)\r?\n?([\s\S]*?)```/g;
  const out=[];let last=0,m;
  while((m=re.exec(src))){
    if(m.index>last)out.push({type:"md",text:src.slice(last,m.index)});
    out.push({type:"code",lang:(m[1]||"").trim(),code:m[2].replace(/\r?\n$/,"")});
    last=re.lastIndex;
  }
  if(last<src.length)out.push({type:"md",text:src.slice(last)});
  return out;
}
// One fenced code block with a toolbar: language label, Copy, and — for shell
// commands, when a terminal is reachable — a "Run in terminal" button that copies
// the command and types it into the project's terminal (the same click-to-run
// flow the health popovers use for host commands).
const CodeBlock=React.memo(function CodeBlock({code,lang,onRunCmd}){
  const shell=isShellLang(lang);
  const run=(e)=>{e.stopPropagation();copyText(code).catch(()=>{});onRunCmd&&onRunCmd(code);};
  return <div className="cblock">
    <div className="cblock-bar">
      <span className="cblock-lang">{lang||(shell?"shell":"code")}</span>
      <span className="sp"/>
      {shell&&onRunCmd&&<button type="button" className="cblock-btn run" title="Copy this command and type it into the project's terminal" onClick={run}>▶ Run in terminal</button>}
      <CopyBtn text={code} cls="cblock-btn"/>
    </div>
    <pre className="cblock-pre"><code>{code}</code></pre>
  </div>;
});
// Render one assistant text part: prose via Markdown, fenced code via CodeBlock.
// No copy button here on purpose: a message can render several AssistantText parts
// (text → tools → text), so a per-part button stacked up as duplicates. The single
// floating copy button lives on the .cmsg wrapper and copies the whole message.
// `onRunCmd` (when the message is in a dock with a terminal) enables the
// per-command Run-in-terminal button.
const AssistantText=React.memo(function AssistantText({text,onRunCmd}){
  // Component-local derivation of an immutable string: memo on [text] alone.
  const segs=useMemo(()=>splitCodeBlocks(text),[text]);
  return <div className="atext">
    {segs.map((s,i)=> s.type==="code"
      ? <CodeBlock key={i} code={s.code} lang={s.lang} onRunCmd={onRunCmd}/>
      : (s.text.trim()? <Md key={i} className="bubble" dir={dirOf(s.text)} text={s.text}/> : null))}
  </div>;
});

export {escapeHtml, mdToHtml, HEB_RE, dirOf, Md, copyText, chatToText, CopyBtn,
  SHELL_LANGS, isShellLang, splitCodeBlocks, CodeBlock, AssistantText};
